import { useModelLabel } from "@judo/i18n";
import type { Input, TimeInput } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import type { TextFieldProps } from "@mui/material/TextField";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { isValid, parse } from "date-fns";
import { buildIconAdornment } from "../utils/icon-adornment";

export interface TimeInputComponentProps {
	element: TimeInput | Input;
	readOnly?: boolean;
	value?: string | Date | null;
	onChange?: (value: Date | null) => void;
	error?: string;
	disabled?: boolean;
	judoClassName?: string;
}

/**
 * Parse a time string (`HH:mm:ss` or `HH:mm`) to a `Date` whose date
 * components default to today and whose time components match the input.
 *
 * Uses `date-fns` `parse` so the parser is consistent with the
 * `LocalizationProvider` (`AdapterDateFns`) used by the picker itself.
 */
function parseTimeToDate(value: string | Date | null | undefined): Date | null {
	if (value == null) return null;
	if (value instanceof Date) return isValid(value) ? value : null;
	if (!value) return null;

	const reference = new Date();
	const withSeconds = parse(value, "HH:mm:ss", reference);
	if (isValid(withSeconds)) return withSeconds;

	const withoutSeconds = parse(value, "HH:mm", reference);
	return isValid(withoutSeconds) ? withoutSeconds : null;
}

/**
 * Time input component using MUI X TimePicker.
 */
export function TimeInputComponent({
	element,
	readOnly,
	value,
	onChange,
	error,
	disabled,
	judoClassName,
}: TimeInputComponentProps) {
	const testId = getInputTestId(element);
	const { label } = useModelLabel(element);
	const startAdornment = buildIconAdornment(element.icon);

	return (
		<TimePicker
			label={label}
			views={["hours", "minutes", "seconds"]}
			className={judoClassName}
			ampm={false}
			ampmInClock={false}
			value={parseTimeToDate(value)}
			onChange={(newValue) => onChange?.(newValue)}
			disabled={disabled ?? element.disabled}
			readOnly={readOnly ?? element.isReadOnly}
			slotProps={{
				// Cast: MUI X's slot type strips arbitrary attributes, but `data-testid`
				// is a valid HTML attribute on the underlying TextField root.
				textField: {
					"data-testid": testId,
					error: !!error,
					helperText: error,
					required: element.required,
					fullWidth: true,
					slotProps: {
						input: {
							startAdornment,
						},
						inputLabel: {
							shrink: true,
						},
					},
				} as TextFieldProps,
			}}
		/>
	);
}
